import { memo, useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Sidebar } from 'primereact/sidebar';
import Library from '../Library';
import ManageMath from './ManageMath';
import { CONSTANTS }  from 'components/HHQComponents';
import { consoleClearProd }  from 'components/HHQComponents';
import './index.css';

interface Props {
  initialValue: string;
  handleEditorChange: (content: string) => void;
  isBasic?: boolean;
  baseImageUrl: string;
  apiService: any;
  toast: any;
  setShowLoading: (loading: boolean) => void;
  isShowCustomMathJax?: boolean;
  onInit?: (evt: any, editor: any) => void;
}

const TinyMCEEditorComponent = ({
  initialValue,
  handleEditorChange,
  isBasic = false,
  baseImageUrl,
  apiService,
  toast,
  setShowLoading,
  isShowCustomMathJax = false,
  onInit,
}: Props) => {
  const editorRef = useRef<any>(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showMath, setShowMath] = useState(false);
  const [content, setContent] = useState(initialValue);
  const [firstLoad, setFirstLoad] = useState(false);
  const [isLocal, setIsLocal] = useState(false);

  useEffect(() => {
    /*setIsLocal(
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    );*/
  }, []);

  consoleClearProd();

  useEffect(() => {
    if (initialValue && !firstLoad) {
      setContent(initialValue);
      setFirstLoad(true);
    }
  }, [initialValue, firstLoad]);

  const menubar = isBasic ? '' : 'file edit insert view format tools table help';

  const toolbar = `undo redo | formatselect fontfamily fontsize | blocks | bold italic backcolor | alignleft aligncenter alignright alignjustify | mathjax ${
    isShowCustomMathJax ? 'custom-match' : ''
  } ${isBasic ? '' : 'library | h5p '} code  | table pageembed bullist numlist outdent indent | removeformat pagebreak emoticons lineheight forecolor blockquote strikethrough superscript help`;

  const pluginString = `advlist autolink lists link ${
    isBasic ? '' : 'image'
  } charmap print preview anchor searchreplace visualblocks code fullscreen insertdatetime media table paste help wordcount table pagebreak hr emoticons`;

  const plugins = pluginString
  .split(' ')
  .map(p => p.trim())
  .filter(p => p.length > 0);
  return (
    <div>
      <Sidebar visible={showLibrary} fullScreen onHide={() => setShowLibrary(false)}>
        <Library
          callFromAdmin={1}
          chooseImage={(file) => {
            const insert = (html: string) => {
              editorRef.current.execCommand('mceInsertContent', false, html);
            };
            const url = baseImageUrl + file.url;
            if (file.type === 'video') insert(`<video controls><source src="${url}">Không hỗ trợ định dạng video.</video>`);
            else if (file.type === 'image') insert(`<img src="${url}" />`);
            else if (file.type === 'audio') insert(`<audio src="${url}" controls="controls"></audio>`);
            else insert(`<a target="_blank" href="${url}">${file.name || 'File'}</a>`);
            setShowLibrary(false);
          }}
          apiService={apiService}
          baseImageUrl={baseImageUrl}
          toast={toast}
          setShowLoading={setShowLoading}
        />
      </Sidebar>

      <Sidebar visible={showMath} className="w-1000px" position="right" onHide={() => setShowMath(false)}>
        <ManageMath
          handleInsertMath={(latex: string) => {
            setShowMath(false);
            editorRef.current.insertContent(`<span class="math-tex">\\( ${latex} \\)</span>`);
            editorRef.current.defaultView.MathJax.startup.getComponents();
            editorRef.current.defaultView.MathJax.typeset();
          }}
        />
      </Sidebar>

      <Editor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        onInit={(_evt, editor) => {
          editorRef.current = editor;
          editor.on('click', () => {});
          if (onInit) {
            onInit(_evt, editor);
          }
        }}
        value={content}
        disabled={isLocal}
        onEditorChange={(e) => {
          setContent(e);
          handleEditorChange(e);
        }}
        init={{
          license_key: 'gpl',
          height: 400,
          external_plugins: {
            mathjax: '/js/tinymce-mathjax/plugin.js',
          },
          branding: false, // ẩn "Build with TinyMCE"
          init_instance_callback: (editor) => {
            // Xoá nút quảng cáo
            const branding = document.querySelector('.tox-statusbar__branding');
            if (branding) branding.remove();

            const getFeatures = document.querySelector('.tox-promotion');
            if (getFeatures) getFeatures.remove();
          },
          fontsize_formats:
            '6px 8px 9px 10px 11px 12px 13px 14px 15px 16px 17px 18px 19px 20px 21px 22px 23px 24px 25px 30px 35px 40px 50px 60px 70px 80px 90px 100px 200px 250px 300px 350px 400px 450px 500px',
          force_br_newlines: false,
          resize: 'both',
          menubar,
          toolbar_mode: 'wrap', // Hiển thị toolbar trên nhiều dòng nếu quá dài
          placeholder: 'Nhập dữ liệu...',
          plugins: plugins,
          toolbar,
          content_style:
            "body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 15px }",
          setup: (editor) => {
            editor.on('init', () => {
              editor.selection.select(editor.getBody(), true);
              editor.selection.collapse(false);
            });
            editor.ui.registry.addIcon('h5p-icon', `<b>H5P</b>`);
            editor.ui.registry.addIcon(
              'image-icon',
              '<svg title = "user: admin | password: Admin123456!@" width="24" height="24" focusable="false"><path d="M5 15.7l3.3-3.2c.3-.3.7-.3 1 0L12 15l4.1-4c.3-.4.8-.4 1 0l2 1.9V5H5v10.7zM5 18V19h3l2.8-2.9-2-2L5 17.9zm14-3l-2.5-2.4-6.4 6.5H19v-4zM4 3h16c.6 0 1 .4 1 1v16c0 .6-.4 1-1 1H4a1 1 0 01-1-1V4c0-.6.4-1 1-1zm6 8a2 2 0 100-4 2 2 0 000 4z" fill-rule="nonzero"></path></svg>'
            );
            editor.ui.registry.addToggleButton('h5p', {
              icon: 'h5p-icon',
              text: '',
              onAction: () => {
                window.open(CONSTANTS.H5P, '_blank');
              },
            });
            editor.ui.registry.addToggleButton('library', {
              icon: 'image-icon',
              text: 'Thư viện',
              onAction: () => setShowLibrary(true),
            });
            /*editor.ui.registry.addButton('mathjax', {
              icon: 'insert-time',
              tooltip: 'Chèn công thức toán',
              onAction: () => {
                setShowMath(true); // Mở giao diện bạn muốn
              }
            });*/
          },
          mathjax: {
            lib: '/js/tinymce-mathjax/tex-mml-chtml.js',
            symbols: { start: '\\(', end: '\\)' },
            className: 'math-tex',
          },
          mobile: {
            menubar: true,
            plugins: plugins,
            toolbar,
          },
        }}
      />
    </div>
  );
};

export default memo(TinyMCEEditorComponent);